import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getFirestore, collection, onSnapshot, doc, getDocs, query, where, writeBatch, arrayRemove } from 'firebase/firestore';
import { onIdTokenChanged } from 'firebase/auth';
import { app, auth } from '../firebase';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

const db = getFirestore(app);

export default function RoomsList() {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const navigate = useNavigate();

  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  const [userDomainId, setUserDomainId] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [domains, setDomains] = useState([]);

  useEffect(() => {
    const unsub = onIdTokenChanged(auth, async (user) => {
      if (!user) {
        setIsSystemAdmin(false);
        setUserDomainId(null);
        setAuthChecked(true);
        return;
      }
      try {
        const tokenResult = await user.getIdTokenResult();
        setIsSystemAdmin(tokenResult?.claims?.role === 'SYSTEM_ADMIN');
        setUserDomainId(tokenResult?.claims?.domainId || null);
      } catch (e) {
        console.error(e);
      } finally {
        setAuthChecked(true);
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!isSystemAdmin) return;
    const unsub = onSnapshot(collection(db, 'domains'), (snap) => {
      setDomains(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [isSystemAdmin]);

  useEffect(() => {
    if (!authChecked) return;

    // For non-System Admins, we MUST have the domainId before we can construct a valid query
    if (!isSystemAdmin && !userDomainId) {
      setLoading(false);
      return;
    }

    let q = query(collection(db, 'rooms'));

    // Domain Admins MUST filter by domainId to satisfy security rules
    if (!isSystemAdmin && userDomainId) {
      q = query(collection(db, 'rooms'), where('domainId', '==', userDomainId));
    }

    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      setRooms(list);
      setLoading(false);
    }, (err) => {
      console.error('rooms snapshot', err);
      setLoading(false);
    });
    return () => unsub();
  }, [authChecked, isSystemAdmin, userDomainId]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete room? This will also un-link the room from any participants.')) return;
    setDeleting(id);
    try {
      // Find participants that reference this room
      const participantsQuery = query(collection(db, 'participants'), where('linkedRoomIds', 'array-contains', id));
      const participantsSnap = await getDocs(participantsQuery);

      // Use a batch to remove the room id from participant docs and delete the room
      const batch = writeBatch(db);
      participantsSnap.forEach(p => {
        const pRef = doc(db, 'participants', p.id);
        batch.update(pRef, { linkedRoomIds: arrayRemove(id) });
      });
      // delete the room document
      batch.delete(doc(db, 'rooms', id));
      await batch.commit();
    } catch (e) {
      console.error('delete room', e);
      alert('Failed to delete room');
    } finally {
      setDeleting(null);
    }
  };

  const getDomainName = (id) => {
    const domain = domains.find(d => d.id === id);
    return domain ? domain.name : id;
  };

  if (loading) return <Box sx={{ p: 2 }}>Loading rooms...</Box>;

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Rooms</Typography>
        <Button variant="contained" onClick={() => navigate('/rooms/new')}>Create Room</Button>
      </Box>
      {rooms.length === 0 ? (
        <Typography>No rooms found.</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                {isSystemAdmin && <TableCell>Domain</TableCell>}
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rooms.map(r => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.name}</TableCell>
                  {isSystemAdmin && <TableCell>{getDomainName(r.domainId)}</TableCell>}
                  <TableCell>
                    <Button component={Link} to={`/rooms/${r.id}`} size="small">View</Button>
                    <Button component={Link} to={`/rooms/${r.id}/edit`} size="small">Edit</Button>
                    <Button onClick={() => handleDelete(r.id)} size="small" color="error" disabled={deleting === r.id}>{deleting === r.id ? 'Deleting...' : 'Delete'}</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
